import { useCallback } from 'react';
import { useData, UseDataOptions, UseDataResult } from './useData';
import { getPoetById } from '@/services/poetService';
import type { Database } from '@/types/supabase';
import { ServiceResult } from '@/utils/service/serviceUtils';
import { AppError, ErrorType } from '@/utils/error/errorHandler';

// Define the Poet type based on the database schema
type Poet = Database['public']['Tables']['poets']['Row'];

// Cache TTL constants
const CACHE_TTL = {
  SHORT: 1 * 60 * 1000, // 1 minute
  MEDIUM: 5 * 60 * 1000, // 5 minutes
  LONG: 30 * 60 * 1000, // 30 minutes
};

// Cache key prefix for poets
const POET_CACHE_KEY_PREFIX = 'poet:id:';

export interface UsePoetOptions extends Omit<UseDataOptions<ServiceResult<Poet>>, 'fetchFn' | 'cacheKey' | 'cacheTTL'> {
  // The ID of the poet to fetch
  poetId: string;
  
  // Whether to use caching (defaults to true)
  useCache?: boolean;
  
  // Custom cache TTL (defaults to MEDIUM)
  cacheTTL?: number;
}

/**
 * Hook for fetching a single poet by ID
 * 
 * @param options - Configuration options for the hook
 * @returns Object containing the poet data, loading state, and error
 */
export function usePoet(
  options: UsePoetOptions
): UseDataResult<ServiceResult<Poet>> {
  const { 
    poetId, 
    useCache = true, 
    cacheTTL = CACHE_TTL.MEDIUM,
    ...restOptions 
  } = options;

  // Create a fetch function for the poet
  const fetchPoet = useCallback(
    async () => {
      // Stricter check: ensure poetId is a non-empty string
      if (typeof poetId !== 'string' || !poetId) { 
        console.warn(`usePoet: Invalid or missing poetId provided: ${poetId}. Skipping fetch.`);
        return { 
          data: null, 
          error: new AppError("Poet ID is required", ErrorType.VALIDATION)
        };
      }
      return getPoetById(poetId);
    },
    [poetId]
  );

  // Create a cache key for the poet
  const cacheKey = useCache ? `${POET_CACHE_KEY_PREFIX}${poetId}` : undefined;

  // Use the generic useData hook
  return useData<ServiceResult<Poet>>({
    ...restOptions,
    fetchFn: fetchPoet,
    cacheKey,
    cacheTTL,
    dependencies: [poetId, ...(restOptions.dependencies || [])]
  });
} 